import React from "react";
import { Box, Typography, Grid } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import Skeleton from "@material-ui/lab/Skeleton";
import moment from "moment";

const AVATAR_COLORS = [
  '#3b82f6', '#10b981', '#f59e0b', '#ef4444',
  '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16',
];

function getAvatarColor(name) {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function getInitials(name) {
  const parts = name.trim().split(' ').filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

function formatTime(minutes) {
  return moment().startOf('day').add(minutes || 0, 'minutes').format('HH[h] mm[m]');
}

const useStyles = makeStyles((theme) => ({
  card: {
    borderRadius: 12,
    border: '1px solid #e5e7eb',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    padding: '16px',
    backgroundColor: theme.palette.background.paper,
    transition: 'box-shadow 0.2s ease',
    height: '100%',
    boxSizing: 'border-box',
    '&:hover': {
      boxShadow: '0 4px 16px rgba(0,0,0,0.09)',
    },
  },
  metricLabel: {
    fontSize: 10,
    fontWeight: 600,
    color: '#9ca3af',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: 2,
  },
  metricValue: {
    fontSize: 14,
    fontWeight: 600,
    color: theme.palette.type === 'dark' ? theme.palette.text.primary : '#111827',
    lineHeight: 1.3,
  },
}));

export default function TableAttendantsStatus({ loading, attendants }) {
  const classes = useStyles();

  if (loading) {
    return (
      <Grid container spacing={2}>
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <Grid item xs={12} sm={6} md={4} key={i}>
            <Skeleton variant="rect" height={148} style={{ borderRadius: 12 }} />
          </Grid>
        ))}
      </Grid>
    );
  }

  if (!attendants || attendants.length === 0) {
    return (
      <Box style={{ textAlign: 'center', padding: '40px 0', color: '#9ca3af' }}>
        <Typography style={{ fontSize: 14 }}>Nenhum atendente encontrado</Typography>
      </Box>
    );
  }

  return (
    <Grid container spacing={2}>
      {attendants.map((a, k) => {
        const color = getAvatarColor(a.name || '');
        const initials = getInitials(a.name || '?');
        const ratingVal = a.rating != null ? Number(a.rating) : null;
        const stars = ratingVal != null ? Math.min(Math.round(ratingVal), 5) : 0;

        return (
          <Grid item xs={12} sm={6} md={4} key={k}>
            <Box className={classes.card}>
              {/* Header: avatar + nome + badge online */}
              <Box style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
                <Box style={{
                  width: 40, height: 40, borderRadius: '50%',
                  backgroundColor: `${color}1a`,
                  border: `2px solid ${color}40`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  <Typography style={{ fontSize: 13, fontWeight: 700, color, lineHeight: 1 }}>
                    {initials}
                  </Typography>
                </Box>
                <Box style={{ flex: 1, minWidth: 0 }}>
                  <Typography style={{
                    fontSize: 14, fontWeight: 600,
                    color: '#111827',
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>
                    {a.name}
                  </Typography>
                  <span style={{
                    display: 'inline-block',
                    marginTop: 3,
                    padding: '2px 8px',
                    borderRadius: 20,
                    fontSize: 10,
                    fontWeight: 600,
                    letterSpacing: '0.2px',
                    background: a.online ? '#d1fae5' : '#f3f4f6',
                    color: a.online ? '#065f46' : '#6b7280',
                  }}>
                    {a.online ? '● Online' : '○ Offline'}
                  </span>
                </Box>
              </Box>

              {/* Métricas em grid 2x2 */}
              <Box style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 16px' }}>
                <Box>
                  <Typography className={classes.metricLabel}>Avaliação</Typography>
                  <Typography className={classes.metricValue}>
                    {ratingVal != null ? ratingVal.toFixed(1) : '—'}
                    {stars > 0 && (
                      <span style={{ fontSize: 11, color: '#f59e0b', marginLeft: 4, letterSpacing: '-1px' }}>
                        {'★'.repeat(stars)}
                      </span>
                    )}
                  </Typography>
                </Box>
                <Box>
                  <Typography className={classes.metricLabel}>Avaliados</Typography>
                  <Typography className={classes.metricValue}>{a.countRating || 0}</Typography>
                </Box>
                <Box>
                  <Typography className={classes.metricLabel}>Tickets</Typography>
                  <Typography className={classes.metricValue}>{a.tickets || 0}</Typography>
                </Box>
                <Box>
                  <Typography className={classes.metricLabel}>T.M. Atend.</Typography>
                  <Typography className={classes.metricValue}>{formatTime(a.avgSupportTime)}</Typography>
                </Box>
              </Box>
            </Box>
          </Grid>
        );
      })}
    </Grid>
  );
}
